import { useInterval } from "@rio-cloud/rio-uikit";
import { useEffect, useRef, useState } from "react";

type Args = {
  /**
   * Function that will be called on refresh
   */
  callback: () => void;

  /**
   * How long should the refresh delay be (in milliseconds)
   * @default 2000
   */
  delay?: number;
};

/**
 * Hook that automatically refreshes data at regular intervals when the window has focus.
 * The callback runs immediately on mount, then continues at the specified interval.
 * Automatically pauses when the window loses focus and resumes when it regains focus.
 *
 * @param callback - Function to be called on refresh
 * @param delay - How long should the refresh delay be (in milliseconds, default: 2000)
 *
 * @example
 * ```tsx
 * function AlertPage() {
 *   const fetcher = useFetcher();
 *
 *   // Fetch data immediately on mount, then every 10 seconds
 *   useRefresh({
 *     callback: () => fetcher.load("/api/alerts"),
 *     delay: 10_000
 *   });
 *
 *   return (
 *     <div>
 *       <h1>Alerts {fetcher.state !== "idle" && <Spinner />}</h1>
 *       <AlertsList alerts={fetcher.data ?? []} />
 *     </div>
 *   );
 * }
 * ```
 *
 * @example
 * ```tsx
 * function LiveChat() {
 *   const [messages, setMessages] = useState([]);
 *
 *   // Poll for new messages every 2 seconds (default delay)
 *   useRefresh({
 *     callback: async () => {
 *       const newMessages = await fetchMessages();
 *       setMessages(newMessages);
 *     }
 *   });
 *
 *   return <MessageList messages={messages} />;
 * }
 * ```
 */
export function useRefresh({ callback, delay = 2000 }: Args) {
  const [shouldRun, setShouldRun] = useState(
    typeof document !== "undefined" && document.hasFocus()
  );

  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const lastRunRef = useRef<number>(0);


  useEffect(() => {
    callbackRef.current();
    lastRunRef.current = Date.now();
  }, []);

  useEffect(() => {
    const onFocus = () => {
      const now = Date.now();
      if (now - lastRunRef.current >= delay) {
        callbackRef.current();
        lastRunRef.current = now;
      }
      setShouldRun(true);
    };
    const onBlur = () => setShouldRun(false);

    window.addEventListener("focus", onFocus);
    window.addEventListener("blur", onBlur);

    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("blur", onBlur);
    };
  }, []);

  useInterval(() => {
    callbackRef.current();
    lastRunRef.current = Date.now();
  }, shouldRun ? delay : null);
}
